import { useMemo, useState } from 'react'
import { Button, Card, Divider, Flex, Text, Title } from '@tremor/react'
import type { InputsState } from './types'
import { getLabels } from './labels'
import { analyze } from './utils'
import Stepper from './steps/Stepper'
import ProviderStep from './steps/ProviderStep'
import ComputeStep from './steps/ComputeStep'
import DatabaseStep from './steps/DatabaseStep'
import StorageStep from './steps/StorageStep'
import NetworkStep from './steps/NetworkStep'
import Results from './Results'
import ContactStep from './steps/ContactStep'
import { useApiV1LeadCreate } from '../../api/api.gen'
import type { EntitiesCreateLeadRequest } from '../../api/api'
import WelcomeStep from './steps/WelcomeStep'
import WatadLogo from '../../images/watad-large.png'

export default function AwsOptimizationPage() {
    const [inputs, setInputsState] = useState<InputsState>({
        provider: 'aws',
        workload: 'general',
        monthlySpend: 1000,
        email: '',
        ec2Instances: 20,
        ec2AvgUtil: 25,
        savingsPlanCoverage: 0,
        rdsInstances: 4,
        rdsAvgUtil: 30,
        s3StandardTb: 10,
        s3IaTb: 2,
        ebsTb: 5,
        snapshotsTb: 2,
        xferOutGb: 3000,
        unusedEips: 0,
        idleElbs: 0,
    })
    const [current, setCurrent] = useState<number>(0)
    const [recs, setRecs] = useState<ReturnType<typeof analyze>['recs']>([])
    const [total, setTotal] = useState<number>(0)

    const labels = useMemo(() => getLabels(inputs.provider), [inputs.provider])

    const setInputs = (updates: Partial<InputsState>) =>
        setInputsState((prev) => ({ ...prev, ...updates }))

    const steps = [
        'Welcome',
        'Provider',
        'Compute',
        'Database',
        'Storage',
        'Networking',
        'Contact',
        'Results',
    ]

    // Prepare lead creation hook (manual trigger)
    const leadPayload: EntitiesCreateLeadRequest = useMemo(
        () => ({
            email: (inputs.email || '').trim(),
            info: {
                provider: inputs.provider,
                workload: inputs.workload,
                monthlySpend: inputs.monthlySpend,
                ec2Instances: inputs.ec2Instances,
                ec2AvgUtil: inputs.ec2AvgUtil,
                savingsPlanCoverage: inputs.savingsPlanCoverage,
                rdsInstances: inputs.rdsInstances,
                rdsAvgUtil: inputs.rdsAvgUtil,
                s3StandardTb: inputs.s3StandardTb,
                s3IaTb: inputs.s3IaTb,
                ebsTb: inputs.ebsTb,
                snapshotsTb: inputs.snapshotsTb,
                xferOutGb: inputs.xferOutGb,
                unusedEips: inputs.unusedEips,
                idleElbs: inputs.idleElbs,
                source: 'cloud_optimization_wizard',
            },
        }),
        [inputs]
    )
    const { sendNowWithParams: submitLead } = useApiV1LeadCreate(
        // default payload is not auto-sent (we will trigger manually)
        leadPayload,
        {},
        false
    )

    const isEmailValid = () => {
        const e = (inputs.email || '').trim()
        if (e.length === 0) return false
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
    }

    const goNext = () => {
        if (current < steps.length - 1) {
            const nextIdx = current + 1
            if (steps[nextIdx] === 'Results') {
                // Fire-and-forget lead collection before showing results
                try {
                    if ((inputs.email || '').trim().length > 0) {
                        console.log('sending request')
                        submitLead(leadPayload, {})
                    }
                } catch (e) {
                    // ignore errors; do not block results rendering
                }
                const out = analyze(inputs)
                setRecs(out.recs)
                setTotal(out.total)
            }
            setCurrent(nextIdx)
        }
    }

    const goPrev = () => setCurrent((idx) => Math.max(0, idx - 1))

    const renderStep = () => {
        switch (steps[current]) {
            case 'Welcome':
                return <WelcomeStep />
            case 'Provider':
                return <ProviderStep inputs={inputs} setInputs={setInputs} />
            case 'Compute':
                return (
                    <ComputeStep
                        inputs={inputs}
                        setInputs={setInputs}
                        labels={labels}
                    />
                )
            case 'Database':
                return (
                    <DatabaseStep
                        inputs={inputs}
                        setInputs={setInputs}
                        labels={labels}
                    />
                )
            case 'Storage':
                return (
                    <StorageStep
                        inputs={inputs}
                        setInputs={setInputs}
                        labels={labels}
                    />
                )
            case 'Networking':
                return (
                    <NetworkStep
                        inputs={inputs}
                        setInputs={setInputs}
                        labels={labels}
                    />
                )
            case 'Contact':
                return (
                    <ContactStep
                        inputs={inputs}
                        setInputs={setInputs}
                        // labels={labels}
                    />
                )
            case 'Results':
                return <Results recs={recs} total={total} labels={labels} />
            default:
                return null
        }
    }

    return (
        <Flex
            flexDirection="col"
            justifyContent="start"
            className="min-h-screen w-full max-w-full h-fit p-3 md:p-6 space-y-4 bg-gradient-to-l from-black to-teal-500"
        >
            <div className="w-36 md:w-48 mt-4 mb-6 md:mb-8">
                <img src={WatadLogo} alt="Watad Logo" />
            </div>{' '}
            <Card className="bg-black ring-gray-800 border-gray-900">
                <Flex flexDirection="col" className="space-y-4">
                    <Stepper
                        steps={steps}
                        current={current}
                        onStepClick={(idx) => idx < current && setCurrent(idx)}
                    />
                    <Divider />
                    {renderStep()}
                    <Flex
                        justifyContent="end"
                        className="pt-2 gap-2 flex-col-reverse md:flex-row"
                    >
                        <Button
                            // variant="secondary"
                            color="gray"
                            onClick={goPrev}
                            disabled={current === 0}
                            className="w-full md:w-auto"
                        >
                            Back
                        </Button>
                        {current < steps.length - 1 && (
                            <Button
                                color="teal"
                                onClick={goNext}
                                disabled={
                                    steps[current] === 'Contact' &&
                                    !isEmailValid()
                                }
                                className="w-full md:w-auto"
                            >
                                Next
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Card>
        </Flex>
    )
}
